import './partner-apply.css';
/** Partner surface frame: nav, approval gate (B09-002 / C04-002), sign-in and application (B09-001). */
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Outlet, useSearchParams } from 'react-router';
import { useRegisterSurfaceNav, type SurfaceNavItem } from '../../../shell/surface-nav';
import { adminOriginFor, customerOriginFor } from '@qm/tenancy/surface';
import { surfaceForEmailDomain } from '@qm/contracts/identity';
import { Button, EntryLayout, Heading, Panel, Text } from '@qm/ui';
import { useOptionalApp } from '../../../lib/app-context';
import { authDestination, rememberNext, safeNext, tokenFromHash } from '../../../lib/deeplink';
import { SurfaceProvider, useSurface } from '../kit/context';
import { Checkbox, ErrorNote, Field, Notice, Screen } from '../kit/ui';
import { useAction, useResource } from '../kit/resource';
import { partnerApi } from '../api';
import { socialApi } from '../api/social';
import { adsNetworksApi } from '../api/ads-networks';
import { buildApplication, emptyApplyForm, gateFor, isUnderReview, type ApplyForm } from '../lib/logic';
import en from '../i18n/en.json';
import ar from '../i18n/ar.json';
import { agencyCrmApi, type CrmHttp } from '../../../features/agency-crm/api';
import { CRM_SECTIONS, sectionPath } from '../../../features/agency-crm/AgencyCrm';
import '../kit/kit.css';
import './screens.css';
import { urlFieldProps, withHttps } from '../../../lib/url';
import { autofillSupported, passkeyErrorKey, passkeySignIn, signInOptions } from '../../../lib/passkeys';
import { TwoFactor, twoFactorStepFor, type TwoFactorStep } from './twofa';
import { GOOGLE_START_URL, GoogleButton, googleErrorSuffix, googleMfaResume, useGoogleSignin } from '../../../lib/google-signin';

export const CATALOG = { en, ar } as const;

export function usePartnerApi() {
  const { http } = useSurface();
  return useMemo(() => partnerApi(http), [http]);
}

type PNav = [to: string, key: string, icon: SurfaceNavItem['icon'], group: string, tab?: boolean];
const NAV: readonly PNav[] = [
  ['/', 'portal.partners.nav.home', 'home', 'overview', true],
  ['/clients', 'portal.partners.nav.clients', 'team', 'clients', true], ['/access', 'portal.partners.nav.access', 'shield', 'clients'],
  ['/calendar', 'portal.partners.nav.calendar', 'calendar', 'delivery', true], ['/approvals', 'portal.partners.nav.approvals', 'approvals', 'delivery', true], ['/reports', 'portal.partners.nav.reports', 'audit', 'delivery'], ['/content', 'portal.partners.nav.content', 'content', 'delivery'],
  ['/ads/meta', 'portal.partners.nav.meta', 'ads', 'channels'], ['/ads/google', 'portal.partners.nav.google', 'ads', 'channels'],
  ['/locations', 'portal.partners.nav.locations', 'pin', 'channels'], ['/whatsapp', 'portal.partners.nav.whatsapp', 'inbox', 'channels'],
  ['/knowledge/ai-search', 'portal.partners.nav.knowledge', 'content', 'agency'],
  ['/mcp', 'portal.partners.nav.mcp', 'settings', 'agency'], ['/branding', 'portal.partners.nav.branding', 'branding', 'agency'], ['/pricing', 'portal.partners.nav.pricing', 'billing', 'agency'],
];

/** All clients' ads in one table (flag connectors.agency_overview). */
const ADS_OVERVIEW_NAV: PNav = ['/ads/overview', 'connectors.agencyOverview.nav', 'ads', 'channels'];

/** Opportunities (flag crm.opportunities): listed only when GET /v1/opportunities/agency/me answers. The agency's own desk, and buying from Quixo. */
const OPPORTUNITY_NAV: readonly PNav[] = [
  ['/opportunities', 'opportunities.agency.nav', 'leads', 'clients'],
  ['/opportunities/purchases', 'opportunities.purchases.nav', 'billing', 'agency'],
];

/** Social scheduling for clients (flag social.agency_scheduling): listed only when GET /v1/social/agency/availability answers. */
const SOCIAL_NAV: PNav = ['/social', 'portal.partners.nav.social', 'content', 'channels'];

/** TikTok / Snap / LinkedIn Ads (flag connectors.agency_networks): listed only when GET /v1/ads-tiktok/agency/clients answers. */
const ADS_NETWORK_NAV: readonly PNav[] = [
  ['/ads/tiktok', 'portal.partners.nav.tiktok', 'ads', 'channels'], ['/ads/snap', 'portal.partners.nav.snap', 'ads', 'channels'], ['/ads/linkedin', 'portal.partners.nav.linkedin', 'ads', 'channels'],
];

/**
 * The partner menu shown in the shell's left drawer, in five plain-language categories. Security is listed only when the passkey API
 * answers; opportunities and the agency CRM only when their APIs answer.
 */
export function partnerNavItems(t: (key: string) => string, withSecurity: boolean, withOpportunities = false, withCrm = false, withAdsOverview = false, withSocial = false, withAdsNetworks = false): SurfaceNavItem[] {
  const entry = ([to, key, icon, group, tab]: PNav) => ({ to, label: t(key), icon, group: t(`portal.partners.nav.group.${group}`), ...(tab ? { tab: true } : {}) });
  return [
    // ads overview (first of the channels): listed only when GET /v1/connectors/agency/overview/availability answers (404 while connectors.agency_overview is off)
    ...NAV.flatMap((n) => (withAdsOverview && n[0] === '/ads/meta' ? [entry(ADS_OVERVIEW_NAV), entry(n)] : [entry(n)])),
    ...(withSocial ? [entry(SOCIAL_NAV)] : []),
    ...(withOpportunities ? OPPORTUNITY_NAV.map(entry) : []),
    ...(withAdsNetworks ? ADS_NETWORK_NAV.map(entry) : []),
    // agency CRM: listed only when GET /v1/crm/availability answers (404 feature_disabled while crm.partner_crm is off)
    ...(withCrm ? CRM_SECTIONS.map((s) => ({ to: sectionPath('/crm', s), label: t(`crm.agency.nav.${s}`), icon: 'team' as const, group: t('crm.agency.nav.group') })) : []),
    ...(withSecurity ? [{ to: '/security', label: t('portal.partners.nav.security'), icon: 'shield' as const, group: t('portal.partners.nav.group.agency'), testId: 'partner-nav-security' }] : []),
  ];
}

/** Root element for the partner surface: provides context; public pages bypass the gate. */
export function PartnerRoot() {
  return (
    <SurfaceProvider catalog={CATALOG}>
      <Outlet />
    </SurfaceProvider>
  );
}

/** Everything behind approval. Pending/suspended partners see the locked state, never client data. */
export function PartnerGate() {
  const api = usePartnerApi();
  const { t, http } = useSurface();
  const me = useResource('me:partner', api.me);
  // Security (passkeys) is listed only when the passkey API answers: it is 404 while identity.passkeys is off.
  const passkeys = useResource('identity:passkeys', api.passkeys);
  // Opportunities are listed only when the API answers: it is 404 while crm.opportunities is off
  const opportunities = useResource('opportunities:agency', api.opportunitiesMe);
  // the agency CRM is listed only when GET /v1/crm/availability answers (404 while crm.partner_crm is off)
  const crm = useResource('crm:availability', () => agencyCrmApi(http as CrmHttp).availability());
  // the ads overview is listed only when its API answers (404 while connectors.agency_overview is off)
  const adsOverview = useResource('ads-overview:availability', api.adsOverviewAvailability);
  // social scheduling for clients is listed only when its API answers (404 while social.agency_scheduling is off)
  const social = useResource('social:availability', () => socialApi(http).availability());
  // TikTok / Snap / LinkedIn Ads are listed only when the API answers (404 while connectors.agency_networks is off; one flag for all three)
  const adsNetworks = useResource('adsnet:probe', () => adsNetworksApi(http).clients('tiktok'));
  const open = me.status !== 'loading' && gateFor(me.status === 'ready' ? me.data : null, me.status === 'error' ? me.error : undefined) === 'open';
  // the menu lives in the shell's left drawer; nothing is listed until the partner is approved
  useRegisterSurfaceNav(open ? partnerNavItems(t, passkeys.status === 'ready', opportunities.status === 'ready', crm.status === 'ready', adsOverview.status === 'ready', social.status === 'ready', adsNetworks.status === 'ready') : []);
  if (me.status === 'loading') return <Text tone="muted">{t('portal.kit.loading')}</Text>;
  const gate = gateFor(me.status === 'ready' ? me.data : null, me.status === 'error' ? me.error : undefined);
  if (gate !== 'open') return <LockedScreen gate={gate} />;
  return <Outlet />;
}

export function LockedScreen({ gate }: { gate: 'pending' | 'rejected' | 'suspended' | 'signed_out' }) {
  const { t } = useSurface();
  if (gate === 'signed_out') return <SignInScreen />;
  const title = gate === 'pending' ? t('partners.pending.title') : t(`portal.partners.locked.${gate}.title`);
  const body = gate === 'pending' ? t('partners.pending.body') : t(`partners.errors.partner_${gate}`);
  return (
    <Screen title={title} testId="partner-locked">
      <Panel data-gate={gate}>
        <Text>{body}</Text>
        <Text tone="muted">{t('portal.partners.locked.noClientAccess')}</Text>
      </Panel>
    </Screen>
  );
}

/**
 * Partner sign-in (C04-002). Only partner memberships get a partner session; the server decides.
 * Passkeys (flag identity.passkeys): "Sign in with a passkey" + autofill on the email field, shown only once the
 * API answers the sign-in options call (404 while the flag is off). Partner passkeys are bound to this host.
 */
export function SignInScreen() {
  const { t, dir, locale } = useSurface();
  const api = usePartnerApi();
  const app = useOptionalApp();
  const [params] = useSearchParams();
  const next = safeNext(params.get('next'));
  const [email, setEmail] = useState('');
  const [passkeyOn, setPasskeyOn] = useState(false);
  const [passkeyError, setPasskeyError] = useState<string | null>(null);
  const [linkError, setLinkError] = useState(false);
  // set once a sign-in link (or an older session) is waiting on a second factor: the only way on is this step
  // (back from Google, GET /me answers 401 while a factor is pending, so the callback names the step: lib/google-signin.tsx)
  const [twoFactor, setTwoFactor] = useState<{ step: TwoFactorStep; methods?: string[] } | null>(() => {
    const resume = googleMfaResume(params);
    const step = resume ? twoFactorStepFor(resume.next) : null;
    return step ? { step, methods: resume?.methods } : null;
  });
  const googleOn = useGoogleSignin();
  const googleError = googleErrorSuffix(params.get('error'));
  const act = useAction(api.requestSignIn);
  // Staff accounts have no magic link: a @quixohub.com / @quixomarketing.com email goes to Microsoft sign-in on the admin site.
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (surfaceForEmailDomain(email) === 'admin') { window.location.assign(`${adminOriginFor(window.location.host, window.location.protocol)}/bff/v1/identity/entra/start`); return; }
    void act.run(email.trim());
  };
  // Owner request 27 Sep 2026: a @quixohub.com / @quixomarketing.com email typed here belongs on the staff
  // sign-in. Domain-only, client-side, no account lookup - see @qm/contracts/identity surfaceForEmailDomain.
  const isStaffEmail = surfaceForEmailDomain(email) === 'admin';
  const done = (dest = '/') => window.location.assign(dest);
  useEffect(() => {
    let live = true;
    void signInOptions().then(async (options) => {
      if (!options || !live) return;
      setPasskeyOn(true);
      if (await autofillSupported()) passkeySignIn({ options, autofill: true }).then(() => done(authDestination(next)), () => undefined);
    });
    return () => { live = false; };
  }, []);
  const withPasskey = async () => {
    setPasskeyError(null);
    try { await passkeySignIn(); done(authDestination(next)); } catch (err) { setPasskeyError(t(passkeyErrorKey(err))); }
  };
  useEffect(() => { if (params.get('next')) rememberNext(params.get('next')); }, [params]);
  useEffect(() => {
    if (typeof window === 'undefined' || tokenFromHash(window.location.hash)) return;
    let live = true;
    void api.identityMe().then((m) => {
      const step = twoFactorStepFor(m?.mfa?.state);
      if (live && step) setTwoFactor({ step, methods: m.mfa.methods });
    }, () => undefined);
    return () => { live = false; };
  }, []);
  useEffect(() => {
    if (app?.me && params.get('next')) window.location.assign(authDestination(next));
  }, [app?.me, next, params]);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const token = tokenFromHash(window.location.hash);
    if (!token) return;
    const dest = authDestination(next);
    history.replaceState(null, '', window.location.pathname + window.location.search);
    void api.consumeSignIn(token).then((r) => {
      const step = twoFactorStepFor(r.next);
      if (step) setTwoFactor({ step, methods: r.mfaMethods }); else window.location.assign(dest);
    }, () => setLinkError(true));
    // the token is single-use; consume once per page load
  }, []);
  return (
    <EntryLayout art="partner" testId="partner-signin" dir={dir} lang={locale} eyebrow={t('portal.partners.signin.eyebrow')}
      headline={t('portal.partners.signin.heroTitle')} pivot={t('portal.partners.signin.heroPivot')} note={t('portal.partners.signin.note')}>
      <Heading level={1} id="page-title">{t('portal.partners.signin.title')}</Heading>
      <Text tone="muted">{t('portal.partners.signin.subtitle')}</Text>
      {twoFactor ? (
        <>
          <Text tone="muted">{t('portal.partners.twofa.title')}</Text>
          <TwoFactor initial={twoFactor.step} methods={twoFactor.methods} onDone={() => done(authDestination(next))} />
        </>
      ) : act.result ? <Notice testId="signin-sent">{t('portal.partners.signin.sent')}</Notice> : (
        <form onSubmit={submit}>
          <Field label={t('portal.partners.signin.email')}>
            {(id) => <input id={id} type="email" name="email" autoComplete={passkeyOn ? 'username webauthn' : 'email'} required value={email} onChange={(e) => setEmail(e.target.value)} />}
          </Field>
          <Button type="submit" variant="primary" disabled={act.pending || !email}>{t('portal.partners.signin.submit')}</Button>
          <ErrorNote error={act.error} />
        </form>
      )}
      {passkeyOn && !act.result && !twoFactor ? <Button type="button" onClick={() => void withPasskey()} data-testid="partner-passkey-signin">{t('portal.partners.signin.passkey')}</Button> : null}
      {googleOn && !act.result && !twoFactor ? <GoogleButton label={t('portal.partners.signin.google')} onClick={() => window.location.assign(GOOGLE_START_URL)} testId="partner-google-signin" /> : null}
      {googleError && !twoFactor ? <Text role="alert" data-testid="partner-google-error">{t(`portal.partners.signin.googleError.${googleError}`)}</Text> : null}
      {passkeyError ? <Text role="alert">{passkeyError}</Text> : null}
      {linkError ? <Text role="alert">{t('portal.kit.error')}</Text> : null}
      <div className="qm-entry__links">
        <Text tone="muted">{t('portal.partners.signin.customerHint')} <a href={typeof window === 'undefined' ? '/signin' : `${customerOriginFor(window.location.host, window.location.protocol)}/signin`}>{t('portal.partners.signin.customerLink')}</a></Text>
        {isStaffEmail ? (
          <Text tone="muted" data-testid="staff-hint">{t('portal.partners.signin.staffHint')} <a href={typeof window === 'undefined' ? '/signin' : `${adminOriginFor(window.location.host, window.location.protocol)}/signin`} data-testid="staff-signin-link">{t('portal.partners.signin.staffLink')}</a></Text>
        ) : null}
        <Text tone="muted"><a href="/apply">{t('portal.partners.signin.apply')}</a></Text>
      </div>
    </EntryLayout>
  );
}

/** Public partner application (B09-001). */
export function ApplyScreen() {
  const { t, locale } = useSurface();
  const api = usePartnerApi();
  const [form, setForm] = useState<ApplyForm>(emptyApplyForm);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const act = useAction(api.apply);
  const set = <K extends keyof ApplyForm>(k: K, v: ApplyForm[K]) => setForm((f) => ({ ...f, [k]: v }));
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const b = buildApplication({ ...form, website: withHttps(form.website) });
    setErrors(b.errors);
    if (b.ok) void act.run(b.value);
  };
  if (act.result) {
    return (
      <Screen title={t('partners.apply.title')} testId="apply-done">
        <Notice tone="good" testId="apply-pending">{t('partners.apply.submitted')}</Notice>
        <Text tone="muted">{t('portal.partners.apply.pendingSignIn')}</Text>
      </Screen>
    );
  }
  const text = (k: keyof ApplyForm, label: string, type = 'text') => (
    <Field label={label} error={errors[k] ? t(`portal.partners.apply.error.${errors[k]}`) : null}>
      {(id) => <input id={id} name={k} type={type} required={k!=='website'} min={k==='clientCount'?0:undefined} max={k==='clientCount'?1000000:undefined} step={k==='clientCount'?1:undefined} autoComplete={k==='contactEmail'?'email':k==='contactPhone'?'tel':k==='contactName'?'name':k==='companyName'?'organization':k==='designation'?'organization-title':k==='city'?'address-level2':k==='website'?'url':undefined} {...(k==='contactPhone'?{placeholder:'+971 50 123 4567'}:{})} maxLength={k==='website'?2048:k==='contactEmail'?254:k==='companyName'?160:k==='contactPhone'?24:120} value={String(form[k]??'')} onChange={(e) => set(k, e.target.value as never)} {...(type === 'url' ? urlFieldProps(String(form[k]), (v) => set(k, v as never)) : {})} />}
    </Field>
  );
  return (
    <Screen title={t('partners.apply.title')} testId="apply">
      <form onSubmit={submit} noValidate>
        <fieldset disabled={act.pending} className="qm-partner-apply-fields"><legend className="qm-sr-only">{t('partners.apply.title')}</legend>
        <div className="qm-partner-apply-grid">
        {text('companyName', t('partners.apply.companyName'))}
        {text('contactEmail', t('partners.apply.contactEmail'), 'email')}
        {text('contactName', t('partners.apply.contactName'))}
        {text('contactPhone', t('partners.apply.contactPhone'), 'tel')}
        {text('designation', t('partners.apply.designation'))}
        {text('clientCount', t('partners.apply.clientCountExact'), 'number')}
        <Field label={t('partners.apply.country')} error={errors.country?t('portal.partners.apply.error.invalid'):null}>{id=>(<select id={id} name="country" value={form.country} onChange={e=>set('country',e.target.value)} required>
         {['AE','SA','QA','KW','BH','OM','US','GB','IN','PK','CA','AU','DE','FR','EG','JO','LB','SG','MY','ZA','NG','KE','PH','BD','LK','NP','NZ','IE','NL','ES','IT','TR','ID'].map(code=><option key={code} value={code}>{new Intl.DisplayNames([locale],{type:'region'}).of(code)??code}</option>)}
        </select>)}</Field>
        {text('city', t('partners.apply.city'))}
        </div>
        {text('website', t('partners.apply.websiteOptional'), 'url')}
        <Text tone="muted">{t('partners.apply.websiteNote')}</Text>
        </fieldset>
        <Text tone="muted">{t('partners.apply.privacyNotice')}</Text>
        <Checkbox name="acceptTerms" label={t('partners.apply.acceptTerms')} checked={form.acceptTerms} onChange={(v) => set('acceptTerms', v)} />
        {errors.acceptTerms ? <Text tone="muted" role="alert">{t('portal.partners.apply.error.terms')}</Text> : null}
        <Button type="submit" variant="primary" disabled={act.pending}>{t('partners.apply.submit')}</Button>
      </form>
      {act.error ? (isUnderReview(act.error) ? <Notice tone="warn" testId="apply-under-review">{t('partners.errors.application_under_review')}</Notice> : <ErrorNote error={act.error} />) : null}
    </Screen>
  );
}
